/**
 * Comprehensive list of countries with ISO 3166-1 alpha-3 codes
 * Organized with preferred countries at the top
 */

interface Country {
  value: string; // ISO 3166-1 alpha-3 code
  label: string;
  flag?: string; // Optional flag emoji
}

// Preferred countries for quick access
export const PREFERRED_COUNTRIES: Country[] = [
  { value: 'NLD', label: 'Netherlands', flag: '🇳🇱' },
  { value: 'BEL', label: 'Belgium', flag: '🇧🇪' },
  { value: 'DEU', label: 'Germany', flag: '🇩🇪' },
  { value: 'GBR', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'CHE', label: 'Switzerland', flag: '🇨🇭' },
];

// All countries sorted alphabetically
export const ALL_COUNTRIES: Country[] = [
  { value: 'AFG', label: 'Afghanistan', flag: '🇦🇫' },
  { value: 'ALB', label: 'Albania', flag: '🇦🇱' },
  { value: 'DZA', label: 'Algeria', flag: '🇩🇿' },
  { value: 'AND', label: 'Andorra', flag: '🇦🇩' },
  { value: 'AGO', label: 'Angola', flag: '🇦🇴' },
  { value: 'ATG', label: 'Antigua and Barbuda', flag: '🇦🇬' },
  { value: 'ARG', label: 'Argentina', flag: '🇦🇷' },
  { value: 'ARM', label: 'Armenia', flag: '🇦🇲' },
  { value: 'AUS', label: 'Australia', flag: '🇦🇺' },
  { value: 'AUT', label: 'Austria', flag: '🇦🇹' },
  { value: 'AZE', label: 'Azerbaijan', flag: '🇦🇿' },
  { value: 'BHS', label: 'Bahamas', flag: '🇧🇸' },
  { value: 'BHR', label: 'Bahrain', flag: '🇧🇭' },
  { value: 'BGD', label: 'Bangladesh', flag: '🇧🇩' },
  { value: 'BRB', label: 'Barbados', flag: '🇧🇧' },
  { value: 'BLR', label: 'Belarus', flag: '🇧🇾' },
  { value: 'BEL', label: 'Belgium', flag: '🇧🇪' },
  { value: 'BLZ', label: 'Belize', flag: '🇧🇿' },
  { value: 'BEN', label: 'Benin', flag: '🇧🇯' },
  { value: 'BMU', label: 'Bermuda', flag: '🇧🇲' },
  { value: 'BTN', label: 'Bhutan', flag: '🇧🇹' },
  { value: 'BOL', label: 'Bolivia', flag: '🇧🇴' },
  { value: 'BIH', label: 'Bosnia and Herzegovina', flag: '🇧🇦' },
  { value: 'BWA', label: 'Botswana', flag: '🇧🇼' },
  { value: 'BRA', label: 'Brazil', flag: '🇧🇷' },
  { value: 'VGB', label: 'British Virgin Islands', flag: '🇻🇬' },
  { value: 'BRN', label: 'Brunei', flag: '🇧🇳' },
  { value: 'BGR', label: 'Bulgaria', flag: '🇧🇬' },
  { value: 'BFA', label: 'Burkina Faso', flag: '🇧🇫' },
  { value: 'BDI', label: 'Burundi', flag: '🇧🇮' },
  { value: 'KHM', label: 'Cambodia', flag: '🇰🇭' },
  { value: 'CMR', label: 'Cameroon', flag: '🇨🇲' },
  { value: 'CAN', label: 'Canada', flag: '🇨🇦' },
  { value: 'CPV', label: 'Cape Verde', flag: '🇨🇻' },
  { value: 'CYM', label: 'Cayman Islands', flag: '🇰🇾' },
  { value: 'CAF', label: 'Central African Republic', flag: '🇨🇫' },
  { value: 'TCD', label: 'Chad', flag: '🇹🇩' },
  { value: 'CHL', label: 'Chile', flag: '🇨🇱' },
  { value: 'CHN', label: 'China', flag: '🇨🇳' },
  { value: 'COL', label: 'Colombia', flag: '🇨🇴' },
  { value: 'COM', label: 'Comoros', flag: '🇰🇲' },
  { value: 'COG', label: 'Congo', flag: '🇨🇬' },
  { value: 'COD', label: 'Congo (Democratic Republic)', flag: '🇨🇩' },
  { value: 'CRI', label: 'Costa Rica', flag: '🇨🇷' },
  { value: 'HRV', label: 'Croatia', flag: '🇭🇷' },
  { value: 'CUB', label: 'Cuba', flag: '🇨🇺' },
  { value: 'CUW', label: 'Curaçao', flag: '🇨🇼' },
  { value: 'CYP', label: 'Cyprus', flag: '🇨🇾' },
  { value: 'CZE', label: 'Czech Republic', flag: '🇨🇿' },
  { value: 'DNK', label: 'Denmark', flag: '🇩🇰' },
  { value: 'DJI', label: 'Djibouti', flag: '🇩🇯' },
  { value: 'DMA', label: 'Dominica', flag: '🇩🇲' },
  { value: 'DOM', label: 'Dominican Republic', flag: '🇩🇴' },
  { value: 'ECU', label: 'Ecuador', flag: '🇪🇨' },
  { value: 'EGY', label: 'Egypt', flag: '🇪🇬' },
  { value: 'SLV', label: 'El Salvador', flag: '🇸🇻' },
  { value: 'GNQ', label: 'Equatorial Guinea', flag: '🇬🇶' },
  { value: 'ERI', label: 'Eritrea', flag: '🇪🇷' },
  { value: 'EST', label: 'Estonia', flag: '🇪🇪' },
  { value: 'SWZ', label: 'Eswatini', flag: '🇸🇿' },
  { value: 'ETH', label: 'Ethiopia', flag: '🇪🇹' },
  { value: 'FJI', label: 'Fiji', flag: '🇫🇯' },
  { value: 'FIN', label: 'Finland', flag: '🇫🇮' },
  { value: 'FRA', label: 'France', flag: '🇫🇷' },
  { value: 'GAB', label: 'Gabon', flag: '🇬🇦' },
  { value: 'GMB', label: 'Gambia', flag: '🇬🇲' },
  { value: 'GEO', label: 'Georgia', flag: '🇬🇪' },
  { value: 'DEU', label: 'Germany', flag: '🇩🇪' },
  { value: 'GHA', label: 'Ghana', flag: '🇬🇭' },
  { value: 'GIB', label: 'Gibraltar', flag: '🇬🇮' },
  { value: 'GRC', label: 'Greece', flag: '🇬🇷' },
  { value: 'GRD', label: 'Grenada', flag: '🇬🇩' },
  { value: 'GTM', label: 'Guatemala', flag: '🇬🇹' },
  { value: 'GGY', label: 'Guernsey', flag: '🇬🇬' },
  { value: 'GIN', label: 'Guinea', flag: '🇬🇳' },
  { value: 'GNB', label: 'Guinea-Bissau', flag: '🇬🇼' },
  { value: 'GUY', label: 'Guyana', flag: '🇬🇾' },
  { value: 'HTI', label: 'Haiti', flag: '🇭🇹' },
  { value: 'HND', label: 'Honduras', flag: '🇭🇳' },
  { value: 'HKG', label: 'Hong Kong', flag: '🇭🇰' },
  { value: 'HUN', label: 'Hungary', flag: '🇭🇺' },
  { value: 'ISL', label: 'Iceland', flag: '🇮🇸' },
  { value: 'IND', label: 'India', flag: '🇮🇳' },
  { value: 'IDN', label: 'Indonesia', flag: '🇮🇩' },
  { value: 'IRN', label: 'Iran', flag: '🇮🇷' },
  { value: 'IRQ', label: 'Iraq', flag: '🇮🇶' },
  { value: 'IRL', label: 'Ireland', flag: '🇮🇪' },
  { value: 'IMN', label: 'Isle of Man', flag: '🇮🇲' },
  { value: 'ISR', label: 'Israel', flag: '🇮🇱' },
  { value: 'ITA', label: 'Italy', flag: '🇮🇹' },
  { value: 'CIV', label: 'Ivory Coast', flag: '🇨🇮' },
  { value: 'JAM', label: 'Jamaica', flag: '🇯🇲' },
  { value: 'JPN', label: 'Japan', flag: '🇯🇵' },
  { value: 'JEY', label: 'Jersey', flag: '🇯🇪' },
  { value: 'JOR', label: 'Jordan', flag: '🇯🇴' },
  { value: 'KAZ', label: 'Kazakhstan', flag: '🇰🇿' },
  { value: 'KEN', label: 'Kenya', flag: '🇰🇪' },
  { value: 'KIR', label: 'Kiribati', flag: '🇰🇮' },
  { value: 'KWT', label: 'Kuwait', flag: '🇰🇼' },
  { value: 'KGZ', label: 'Kyrgyzstan', flag: '🇰🇬' },
  { value: 'LAO', label: 'Laos', flag: '🇱🇦' },
  { value: 'LVA', label: 'Latvia', flag: '🇱🇻' },
  { value: 'LBN', label: 'Lebanon', flag: '🇱🇧' },
  { value: 'LSO', label: 'Lesotho', flag: '🇱🇸' },
  { value: 'LBR', label: 'Liberia', flag: '🇱🇷' },
  { value: 'LBY', label: 'Libya', flag: '🇱🇾' },
  { value: 'LIE', label: 'Liechtenstein', flag: '🇱🇮' },
  { value: 'LTU', label: 'Lithuania', flag: '🇱🇹' },
  { value: 'LUX', label: 'Luxembourg', flag: '🇱🇺' },
  { value: 'MAC', label: 'Macau', flag: '🇲🇴' },
  { value: 'MDG', label: 'Madagascar', flag: '🇲🇬' },
  { value: 'MWI', label: 'Malawi', flag: '🇲🇼' },
  { value: 'MYS', label: 'Malaysia', flag: '🇲🇾' },
  { value: 'MDV', label: 'Maldives', flag: '🇲🇻' },
  { value: 'MLI', label: 'Mali', flag: '🇲🇱' },
  { value: 'MLT', label: 'Malta', flag: '🇲🇹' },
  { value: 'MHL', label: 'Marshall Islands', flag: '🇲🇭' },
  { value: 'MRT', label: 'Mauritania', flag: '🇲🇷' },
  { value: 'MUS', label: 'Mauritius', flag: '🇲🇺' },
  { value: 'MEX', label: 'Mexico', flag: '🇲🇽' },
  { value: 'FSM', label: 'Micronesia', flag: '🇫🇲' },
  { value: 'MDA', label: 'Moldova', flag: '🇲🇩' },
  { value: 'MCO', label: 'Monaco', flag: '🇲🇨' },
  { value: 'MNG', label: 'Mongolia', flag: '🇲🇳' },
  { value: 'MNE', label: 'Montenegro', flag: '🇲🇪' },
  { value: 'MAR', label: 'Morocco', flag: '🇲🇦' },
  { value: 'MOZ', label: 'Mozambique', flag: '🇲🇿' },
  { value: 'MMR', label: 'Myanmar', flag: '🇲🇲' },
  { value: 'NAM', label: 'Namibia', flag: '🇳🇦' },
  { value: 'NRU', label: 'Nauru', flag: '🇳🇷' },
  { value: 'NPL', label: 'Nepal', flag: '🇳🇵' },
  { value: 'NLD', label: 'Netherlands', flag: '🇳🇱' },
  { value: 'NZL', label: 'New Zealand', flag: '🇳🇿' },
  { value: 'NIC', label: 'Nicaragua', flag: '🇳🇮' },
  { value: 'NER', label: 'Niger', flag: '🇳🇪' },
  { value: 'NGA', label: 'Nigeria', flag: '🇳🇬' },
  { value: 'MKD', label: 'North Macedonia', flag: '🇲🇰' },
  { value: 'NOR', label: 'Norway', flag: '🇳🇴' },
  { value: 'OMN', label: 'Oman', flag: '🇴🇲' },
  { value: 'PAK', label: 'Pakistan', flag: '🇵🇰' },
  { value: 'PLW', label: 'Palau', flag: '🇵🇼' },
  { value: 'PSE', label: 'Palestine', flag: '🇵🇸' },
  { value: 'PAN', label: 'Panama', flag: '🇵🇦' },
  { value: 'PNG', label: 'Papua New Guinea', flag: '🇵🇬' },
  { value: 'PRY', label: 'Paraguay', flag: '🇵🇾' },
  { value: 'PER', label: 'Peru', flag: '🇵🇪' },
  { value: 'PHL', label: 'Philippines', flag: '🇵🇭' },
  { value: 'POL', label: 'Poland', flag: '🇵🇱' },
  { value: 'PRT', label: 'Portugal', flag: '🇵🇹' },
  { value: 'QAT', label: 'Qatar', flag: '🇶🇦' },
  { value: 'ROU', label: 'Romania', flag: '🇷🇴' },
  { value: 'RUS', label: 'Russia', flag: '🇷🇺' },
  { value: 'RWA', label: 'Rwanda', flag: '🇷🇼' },
  { value: 'KNA', label: 'Saint Kitts and Nevis', flag: '🇰🇳' },
  { value: 'LCA', label: 'Saint Lucia', flag: '🇱🇨' },
  { value: 'VCT', label: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
  { value: 'WSM', label: 'Samoa', flag: '🇼🇸' },
  { value: 'SMR', label: 'San Marino', flag: '🇸🇲' },
  { value: 'STP', label: 'São Tomé and Príncipe', flag: '🇸🇹' },
  { value: 'SAU', label: 'Saudi Arabia', flag: '🇸🇦' },
  { value: 'SEN', label: 'Senegal', flag: '🇸🇳' },
  { value: 'SRB', label: 'Serbia', flag: '🇷🇸' },
  { value: 'SYC', label: 'Seychelles', flag: '🇸🇨' },
  { value: 'SLE', label: 'Sierra Leone', flag: '🇸🇱' },
  { value: 'SGP', label: 'Singapore', flag: '🇸🇬' },
  { value: 'SVK', label: 'Slovakia', flag: '🇸🇰' },
  { value: 'SVN', label: 'Slovenia', flag: '🇸🇮' },
  { value: 'SLB', label: 'Solomon Islands', flag: '🇸🇧' },
  { value: 'SOM', label: 'Somalia', flag: '🇸🇴' },
  { value: 'ZAF', label: 'South Africa', flag: '🇿🇦' },
  { value: 'KOR', label: 'South Korea', flag: '🇰🇷' },
  { value: 'SSD', label: 'South Sudan', flag: '🇸🇸' },
  { value: 'ESP', label: 'Spain', flag: '🇪🇸' },
  { value: 'LKA', label: 'Sri Lanka', flag: '🇱🇰' },
  { value: 'SDN', label: 'Sudan', flag: '🇸🇩' },
  { value: 'SUR', label: 'Suriname', flag: '🇸🇷' },
  { value: 'SWE', label: 'Sweden', flag: '🇸🇪' },
  { value: 'CHE', label: 'Switzerland', flag: '🇨🇭' },
  { value: 'SYR', label: 'Syria', flag: '🇸🇾' },
  { value: 'TWN', label: 'Taiwan', flag: '🇹🇼' },
  { value: 'TJK', label: 'Tajikistan', flag: '🇹🇯' },
  { value: 'TZA', label: 'Tanzania', flag: '🇹🇿' },
  { value: 'THA', label: 'Thailand', flag: '🇹🇭' },
  { value: 'TLS', label: 'Timor-Leste', flag: '🇹🇱' },
  { value: 'TGO', label: 'Togo', flag: '🇹🇬' },
  { value: 'TON', label: 'Tonga', flag: '🇹🇴' },
  { value: 'TTO', label: 'Trinidad and Tobago', flag: '🇹🇹' },
  { value: 'TUN', label: 'Tunisia', flag: '🇹🇳' },
  { value: 'TUR', label: 'Turkey', flag: '🇹🇷' },
  { value: 'TKM', label: 'Turkmenistan', flag: '🇹🇲' },
  { value: 'TCA', label: 'Turks and Caicos Islands', flag: '🇹🇨' },
  { value: 'TUV', label: 'Tuvalu', flag: '🇹🇻' },
  { value: 'UGA', label: 'Uganda', flag: '🇺🇬' },
  { value: 'UKR', label: 'Ukraine', flag: '🇺🇦' },
  { value: 'ARE', label: 'United Arab Emirates', flag: '🇦🇪' },
  { value: 'GBR', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'USA', label: 'United States', flag: '🇺🇸' },
  { value: 'URY', label: 'Uruguay', flag: '🇺🇾' },
  { value: 'UZB', label: 'Uzbekistan', flag: '🇺🇿' },
  { value: 'VUT', label: 'Vanuatu', flag: '🇻🇺' },
  { value: 'VAT', label: 'Vatican City', flag: '🇻🇦' },
  { value: 'VEN', label: 'Venezuela', flag: '🇻🇪' },
  { value: 'VNM', label: 'Vietnam', flag: '🇻🇳' },
  { value: 'YEM', label: 'Yemen', flag: '🇾🇪' },
  { value: 'ZMB', label: 'Zambia', flag: '🇿🇲' },
  { value: 'ZWE', label: 'Zimbabwe', flag: '🇿🇼' },
];

// Helper function to get country by code
export function getCountryByCode(code: string): Country | undefined {
  // Check preferred countries first
  const preferred = PREFERRED_COUNTRIES.find((c) => c.value === code);
  if (preferred) return preferred;

  // Then check all countries
  return ALL_COUNTRIES.find((c) => c.value === code);
}

// Helper function to get country label
export function getCountryLabel(code: string): string {
  const country = getCountryByCode(code);
  return country?.label || code;
}
